/**
 * useFriendsMatchesData hook
 * Extracted from app/friends.tsx
 *
 * Manages data fetching, state management, and friend match data
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import type { Friend, FriendMatchSummary, BetSelection } from '../types/friends.types';
import { buildFriendMatchData } from '../helpers/friendsMatchDataBuilder';
import { deduplicateFriendsByEmail } from '../helpers/friendsDeduplication.helper';
// Removed usePickHistoryQuery - pick history not needed on friends page (only winRate is used)
import { useActiveChallengesQuery } from '../../challenges/hooks/useChallengesQuery';
import { useMatchesQuery } from './useMatchesQuery';
import type { ChallengeWithStats } from '../../../types/challenges';

interface UseFriendsMatchesDataProps {
  currentUserId: string;
  isSignedIn: boolean | undefined;
  isLoaded: boolean | undefined;
  friends: Friend[]; // Friends from global context
  // apiClient and refreshFriends are not used in this hook (React Query handles data fetching)
}

interface UseFriendsMatchesDataReturn {
  // State
  loading: boolean;
  friendMatchMap: Record<string, FriendMatchSummary[]>;
  setFriendMatchMap: React.Dispatch<React.SetStateAction<Record<string, FriendMatchSummary[]>>>;
  userSelections: Record<string, BetSelection>;
  setUserSelections: React.Dispatch<React.SetStateAction<Record<string, BetSelection>>>;
  originalSelections: Record<string, BetSelection>;
  setOriginalSelections: React.Dispatch<React.SetStateAction<Record<string, BetSelection>>>;
  expandedFriendId: string | null;
  setExpandedFriendId: React.Dispatch<React.SetStateAction<string | null>>;
  updatingFriendId: string | null;
  setUpdatingFriendId: React.Dispatch<React.SetStateAction<string | null>>;
  // Functions
  loadData: () => Promise<void>;
  getFriendMatches: (friendId: string) => FriendMatchSummary[];
  friendHasChanges: (friendId: string) => boolean;
  deduplicateFriendsByEmail: (friends: Friend[]) => Friend[];
}

export const useFriendsMatchesData = ({
  currentUserId,
  isSignedIn,
  isLoaded,
  friends, // From global context
}: UseFriendsMatchesDataProps): UseFriendsMatchesDataReturn => {
  // NOTE: pickHistory is not needed on friends page - FriendCard only uses friend.winRate
  // which comes from the friends API response, not pick history
  // Removed usePickHistoryQuery to reduce unnecessary API calls

  const { data: challengesData = [], isLoading: isLoadingChallenges } = useActiveChallengesQuery(
    currentUserId,
    isSignedIn === true && isLoaded === true
  );

  // Collect all unique match IDs from challenges
  const allMatchIds = useMemo(() => {
    return [...new Set((challengesData as ChallengeWithStats[]).flatMap((c) => (c.matchIds || []) as string[]))];
  }, [challengesData]);

  // Fetch matches using React Query
  const matchIdsArray: string[] = allMatchIds.length > 0 ? allMatchIds : [];
  const { data: matchesData = [], isLoading: isLoadingMatches } = useMatchesQuery(
    matchIdsArray.length > 0 ? matchIdsArray : undefined,
    isSignedIn === true && isLoaded === true && matchIdsArray.length > 0
  );

  // Combined loading state (pickHistory removed - not needed)
  const loading = isLoadingChallenges || isLoadingMatches;

  // State for UI
  const [userSelections, setUserSelections] = useState<Record<string, BetSelection>>({});
  const [originalSelections, setOriginalSelections] = useState<Record<string, BetSelection>>({});
  const [expandedFriendId, setExpandedFriendId] = useState<string | null>(null);
  const [updatingFriendId, setUpdatingFriendId] = useState<string | null>(null);

  // Build friend match data when data is available (pure computation, no side effects)
  const { friendMatchMap, computedSelections } = useMemo(() => {
    if (!friends.length || !challengesData.length || loading) {
      return { friendMatchMap: {}, computedSelections: {} };
    }

    const { friendMatches, selections } = buildFriendMatchData(friends, challengesData, matchesData, currentUserId);
    return { friendMatchMap: friendMatches, computedSelections: selections };
  }, [friends, challengesData, matchesData, currentUserId, loading]);

  // Track last applied computedSelections to prevent infinite loops when
  // upstream re-renders produce new object references with the same content
  const lastAppliedSelectionsRef = useRef<string>('');

  // Update state when computed data changes (side effects in useEffect, not useMemo)
  useEffect(() => {
    if (!friends.length || !challengesData.length || loading) {
      return;
    }

    // Guard: skip if content hasn't actually changed (prevents loops caused by
    // new object references from React Query background refetches or parent re-renders)
    const selectionsKey = JSON.stringify(computedSelections);
    if (selectionsKey === lastAppliedSelectionsRef.current) {
      return;
    }
    lastAppliedSelectionsRef.current = selectionsKey;

    // Update selections when data changes
    setUserSelections({ ...computedSelections });
    setOriginalSelections({ ...computedSelections });
    setExpandedFriendId((prev) => (prev && friendMatchMap[prev] ? prev : null));
  }, [computedSelections, friendMatchMap, friends.length, challengesData.length, loading]);

  // setFriendMatchMap is not needed - friendMatchMap is computed from React Query data
  const setFriendMatchMap = useCallback(() => {
    // No-op: friendMatchMap is computed, cannot be set directly
  }, []);

  // React Query handles automatic refetching based on staleTime
  // No need for manual polling or loadData - React Query manages this

  // Get matches for a specific friend
  const getFriendMatches = useCallback(
    (friendId: string): FriendMatchSummary[] => {
      const matches = friendMatchMap[friendId] || [];
      return matches;
    },
    [friendMatchMap]
  );

  // Check if friend has unsaved changes
  const friendHasChanges = useCallback(
    (friendId: string): boolean => {
      return getFriendMatches(friendId).some((match) => {
        const current = userSelections[match.matchId] ?? null;
        const original = originalSelections[match.matchId] ?? null;
        return current !== original;
      });
    },
    [getFriendMatches, userSelections, originalSelections]
  );

  return {
    // State
    loading,
    friendMatchMap,
    setFriendMatchMap,
    userSelections,
    setUserSelections,
    originalSelections,
    setOriginalSelections,
    expandedFriendId,
    setExpandedFriendId,
    updatingFriendId,
    setUpdatingFriendId,
    // Functions
    loadData: async () => {
      // No-op: React Query handles data loading automatically via refetch
      // If needed, can call queryClient.invalidateQueries() from parent
    },
    getFriendMatches,
    friendHasChanges,
    deduplicateFriendsByEmail,
  };
};
